import { ThemedTooltip } from '@/components/chat/FeedbackContainer';
import { LOG_MESSAGES } from '@/constants/logMessages';
import useConversationHistory from '@/hooks/useConversationHistory';
import { useChatStore } from '@/hooks/useChatStore';
import type { Conversation } from '@/interface/chat';
import { colors } from '@/theme/colors';
import { logEvent, logger } from '@/util/logger';
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ForumOutlined from '@mui/icons-material/ForumOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import RecentChatLoader from './RecentChatLoader';

export const generateSummary = (
  message: string,
  maxLength: number = 30,
  fallbackNewChat?: string,
  fallbackInProgress?: string,
): string => {
  if (!message || typeof message !== 'string') {
    return fallbackNewChat || '';
  }

  const trimmedMessage = message.trim();
  if (trimmedMessage.length === 0) {
    return fallbackInProgress || '';
  }

  return trimmedMessage.length > maxLength
    ? trimmedMessage.slice(0, maxLength) + '...'
    : trimmedMessage;
};

export const hasValidSummary = (summary?: string): boolean => {
  return !!(summary && summary.trim().length > 0);
};

export const getConversationSummary = (
  summary?: string,
  fallbackMessage?: string,
  fallbackNewChat?: string,
  fallbackInProgress?: string,
): string => {
  const trimmedSummary = summary?.trim();

  if (trimmedSummary && trimmedSummary.length > 0) {
    return trimmedSummary;
  }

  if (fallbackMessage) {
    return generateSummary(
      fallbackMessage,
      30,
      fallbackNewChat,
      fallbackInProgress,
    );
  }

  return fallbackInProgress || '';
};

const RecentChatContainer: FC<{
  setMobileOpen: (open: boolean) => void;
}> = ({ setMobileOpen }) => {
  const { t } = useTranslation(['navMenu']);
  const theme = useTheme();
  const navigate = useNavigate();
  const { chatId } = useParams<{ chatId: string }>();
  const { isProcessingMessage, chatHistory } = useChatStore();
  const {
    sortedChats,
    showMore,
    setShowMore,
    isLoading,
    // addOrUpdateConversation,
  } = useConversationHistory();
  const isNewChatProcessing =
    !chatId && isProcessingMessage && chatHistory.length !== 0;

  // const { profile } = useProfile();
  const handleChatAccess = async (
    conversationId: string,
    conversationSummary: string,
  ) => {
    if (isNewChatProcessing) return;
    try {
      logEvent(LOG_MESSAGES.CONVERSATION_ACCESSED, { chatId: conversationId });

      // Find the current conversation to check if it has a missing summary
      // const currentConversation = sortedChats?.find(
      //   (chat: Conversation) => chat.uId === conversationId,
      // );
      // const hasMissingSummary =
      //   currentConversation &&
      //   !hasValidSummary(currentConversation.converstionSummary);

      // const validSummary = hasMissingSummary
      //   ? currentConversation?.converstionSummary || ''
      //   : hasValidSummary(conversationSummary)
      //     ? conversationSummary
      //     : t('menu.newChat');

      // if (currentConversation) {
      //   if (currentConversation.converstionSummary !== validSummary) {
      //     addOrUpdateConversation({
      //       uId: conversationId,
      //       converstionSummary: validSummary,
      //       updatedOn: currentConversation.updatedOn,
      //       createdOn: currentConversation.createdOn,
      //       expireOn:
      //         currentConversation.expireOn ||
      //         new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      //       userId: profile?.value[0].eXpMember.Uuid ?? 'unknown-user',
      //       chatType: currentConversation.chatType,
      //     });
      //   }
      // } else {
      //   addOrUpdateConversation({
      //     uId: conversationId,
      //     converstionSummary: validSummary,
      //     updatedOn: new Date().toISOString(),
      //     createdOn: new Date().toISOString(),
      //     expireOn: new Date(
      //       Date.now() + 30 * 24 * 60 * 60 * 1000,
      //     ).toISOString(),
      //     userId: profile?.value[0].eXpMember.Uuid ?? 'unknown-user',
      //   });
      // }

      navigate(`/chat/${conversationId}`);
    } catch (error) {
      logger.error({ message: LOG_MESSAGES.CONVERSATION_ACCESS_FAILED, error });
    }
  };
  if (isLoading)
    return (
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <RecentChatLoader />
        <RecentChatLoader />
        <RecentChatLoader />
        <RecentChatLoader />
      </Box>
    );
  return (
    <Box
      data-testid="recent-chat-button-container"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Scrollable Chat List */}
      <Box
        sx={{
          flex: 1,
        }}
      >
        <List sx={{ padding: 0 }}>
          {sortedChats.slice(0, 5).map((chat: Conversation) => {
            const isActive = chatId === chat.uId;
            const hasSummary = hasValidSummary(chat.converstionSummary);

            return (
              <RecentChatButton
                key={chat.uId}
                chatType={chat.chatType}
                conversationId={chat.uId}
                conversationSummary={
                  hasSummary ? (
                    <Fade in={true} easing="ease-in">
                      <span>{chat.converstionSummary}</span>
                    </Fade>
                  ) : (
                    <Box
                      sx={{
                        width: '235px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '6px',
                      }}
                    >
                      {t('menu.newChat')}
                      <CircularProgress size={12} thickness={5} />
                    </Box>
                  )
                }
                summaryString={
                  hasSummary ? chat.converstionSummary : t('menu.newChat')
                }
                isActive={isActive}
                setMobileOpen={setMobileOpen}
                handleChatAccess={handleChatAccess}
                hasMissingSummary={!hasSummary}
              />
            );
          })}
          {sortedChats.length > 5 && (
            <ListItemButton
              data-testid="toggle-recent-chats"
              onClick={() => setShowMore(!showMore)}
              sx={{
                textTransform: 'capitalize',
                textAlign: 'left',
                width: '100%',
                marginBottom: '2px',
                paddingLeft: '2px',
                ':hover': {
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? colors.listItemButton.dark.hover
                      : colors.listItemButton.light.hover,
                },
                borderRadius: '12px',
                position: 'relative',
              }}
            >
              <Box sx={{ width: '100%', marginLeft: '20px' }}>
                <Typography
                  sx={{
                    fontWeight: '400',
                    fontSize: '0.75rem',
                    letterSpacing: '0.4px',
                    color: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    gap: '8px',
                  }}
                >
                  {showMore ? (
                    <>
                      {t('menu.showLess')}
                      <ExpandLessRounded
                        sx={{ height: '18px', width: '18px' }}
                      />
                    </>
                  ) : (
                    <>
                      {t('menu.showMore')}
                      <ExpandMoreRounded
                        sx={{ height: '18px', width: '18px' }}
                      />
                    </>
                  )}
                </Typography>
              </Box>
            </ListItemButton>
          )}
          {showMore &&
            sortedChats
              .slice(5, sortedChats.length)
              .map((chat: Conversation) => {
                const isActive = chatId === chat.uId;
                const hasSummary = hasValidSummary(chat.converstionSummary);

                return (
                  <RecentChatButton
                    key={chat.uId}
                    chatType={chat.chatType}
                    conversationId={chat.uId}
                    conversationSummary={
                      !hasSummary ? (
                        <Box
                          sx={{
                            width: '235px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '6px',
                          }}
                        >
                          {t('menu.newChat')}
                          <CircularProgress size={12} thickness={5} />
                        </Box>
                      ) : (
                        <Fade in={true} easing={'ease-in'}>
                          <span>{chat.converstionSummary}</span>
                        </Fade>
                      )
                    }
                    summaryString={
                      hasSummary ? chat.converstionSummary : t('menu.newChat')
                    }
                    isActive={isActive}
                    setMobileOpen={setMobileOpen}
                    handleChatAccess={handleChatAccess}
                    hasMissingSummary={!hasSummary}
                  />
                );
              })}
        </List>
      </Box>
    </Box>
  );
};

export default RecentChatContainer;

type RecentChatButtonProps = {
  chatType?: 'Voice Mode' | 'Text Mode';
  conversationId: string;
  conversationSummary: React.ReactNode;
  summaryString: string;
  isActive: boolean;
  setMobileOpen: (open: boolean) => void;
  handleChatAccess: (
    conversationId: string,
    conversationSummary: string,
  ) => void;
  hasMissingSummary?: boolean;
};
const RecentChatButton: React.FC<RecentChatButtonProps> = ({
  chatType,
  conversationId,
  conversationSummary,
  summaryString,
  isActive,
  setMobileOpen,
  handleChatAccess,
  hasMissingSummary = false,
}) => {
  const theme = useTheme();

  // Add visual indication for conversations with missing summaries
  const getSummaryStyle = () => {
    if (hasMissingSummary) {
      return {
        fontStyle: 'italic' as const,
        opacity: 0.7,
        color: theme.palette.text.secondary,
      };
    }
    return {};
  };

  return (
    <ThemedTooltip title={summaryString} placement="right">
      <Box sx={{ width: '100%' }}>
        <ListItemButton
          key={conversationId}
          data-testid={`recent-chat-button-${conversationId.replaceAll(' ', '-').replaceAll(',', '').toLowerCase()}`}
          onClick={() => {
            handleChatAccess(conversationId, summaryString);
            setMobileOpen(false);
          }}
          sx={{
            textTransform: 'capitalize',
            display: 'flex',
            textAlign: 'left',
            textOverflow: 'ellipsis',
            marginBottom: '2px',
            marginX: '10px',
            padding: '6px 10px',
            backgroundColor: isActive
              ? theme.palette.mode === 'dark'
                ? colors.listItemButton.dark.selected
                : colors.listItemButton.light.selected
              : '',
            ':hover': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? colors.listItemButton.dark.hover
                  : colors.listItemButton.light.hover,
            },
            borderRadius: '12px',
            position: 'relative',
          }}
        >
          <Typography
            sx={{
              fontWeight: '400',
              fontSize: '13px',
              letterSpacing: '0.4px',
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}
          >
            {chatType === 'Voice Mode' ? (
              <RecordVoiceOverOutlinedIcon
                sx={{
                  color: isActive ? theme.palette.primary.main : colors.gray50,
                  flexShrink: 0,
                }}
              />
            ) : (
              <ForumOutlined
                sx={{
                  color: isActive ? theme.palette.primary.main : colors.gray50,
                  flexShrink: 0,
                }}
              />
            )}
            <Box
              component="span"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                flex: 1,
                ...getSummaryStyle(),
              }}
            >
              {conversationSummary}
            </Box>
          </Typography>
        </ListItemButton>
      </Box>
    </ThemedTooltip>
  );
};
